import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AppLogDoc } from '../DiagnosticDashboard';

interface LogDetailModalProps {
  log: AppLogDoc | null;
  open: boolean;
  onClose: () => void;
}

const LEVEL_COLORS: Record<string, { bg: string; text: string }> = {
  error: { bg: '#FEF2F2', text: '#DC2626' },
  warn:  { bg: '#FFFBEB', text: '#D97706' },
  info:  { bg: '#EFF6FF', text: '#2563EB' },
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.07em"
      display="block"
      mb={0.75}
    >
      {children}
    </Typography>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <Box mb={1.25}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Box>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  if (value === undefined || value === null) return null;
  const isEmpty =
    typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0;
  if (isEmpty) return null;
  return (
    <Box mb={1.25}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          p: 1.25,
          borderRadius: '6px',
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          fontSize: '0.72rem',
          overflow: 'auto',
          maxHeight: 200,
          fontFamily: 'monospace',
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
}

function SectionBox({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        bgcolor: '#FAFAFA',
        mb: 2,
      }}
    >
      {children}
    </Box>
  );
}

export default function LogDetailModal({ log, open, onClose }: LogDetailModalProps) {
  if (!log) return null;

  const levelStyle = LEVEL_COLORS[log.level] ?? { bg: '#F3F4F6', text: '#374151' };
  const meta = log.metadata as Record<string, any> | undefined;
  const isHttpError = log.category === 'http_error';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography fontWeight={700}>Log Detail</Typography>
          <Chip
            label={log.level}
            size="small"
            sx={{
              bgcolor: levelStyle.bg,
              color: levelStyle.text,
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
          <Chip
            label={log.category}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>

        {/* Core info */}
        <SectionBox>
          <SectionLabel>Overview</SectionLabel>
          <Field label="Message" value={log.message} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Field label="Timestamp" value={new Date(log.createdAt).toLocaleString()} />
            <Field label="IP" value={log.ip} />
            <Field label="User" value={log.userEmail} />
            <Field label="Org ID" value={log.orgId} />
          </Stack>
        </SectionBox>

        {/* HTTP error: split into Request + Response */}
        {isHttpError && meta && (
          <>
            <SectionBox>
              <SectionLabel>Request</SectionLabel>
              <Stack direction="row" spacing={3} mb={1}>
                <Field label="Method" value={meta.method} />
                <Field label="Path"   value={meta.path} />
                <Field label="Duration" value={meta.durationMs != null ? `${meta.durationMs}ms` : undefined} />
              </Stack>
              <JsonBlock label="Query Params" value={meta.query} />
              <JsonBlock label="Route Params" value={meta.params} />
              <JsonBlock label="Request Body" value={meta.body} />
            </SectionBox>

            <SectionBox>
              <SectionLabel>Response</SectionLabel>
              <Field label="Status Code" value={meta.statusCode != null ? String(meta.statusCode) : undefined} />
              <JsonBlock label="Response Body" value={meta.responseBody} />
            </SectionBox>
          </>
        )}

        {/* All other categories: raw metadata JSON */}
        {!isHttpError && meta && Object.keys(meta).length > 0 && (
          <SectionBox>
            <SectionLabel>Metadata</SectionLabel>
            <JsonBlock label="" value={meta} />
          </SectionBox>
        )}

      </DialogContent>
    </Dialog>
  );
}
